import posthog from "posthog-js";
import { getSessionId } from "./sessionId";
import {
  MISSING_VOICES_PATH,
  isMissingVoicesHost,
} from "./missingVoicesRouting";
import { PRODUCT_ANALYTICS_APPS } from "./analyticsEvents";

export const PRODUCT_ANALYTICS_CONSENT_KEY = "teachleague_analytics_consent";
const PRODUCT_ANALYTICS_ATTRIBUTION_KEY =
  "teachleague_analytics_attribution";

const posthogKey = process.env.REACT_APP_POSTHOG_KEY;
const posthogHost =
  process.env.REACT_APP_POSTHOG_API_HOST || "https://us.i.posthog.com";
const posthogDebug = process.env.REACT_APP_POSTHOG_DEBUG === "true";

let initialized = false;

const hasWindow = () => typeof window !== "undefined";

const safeStorageGet = (storage, key) => {
  try {
    return storage.getItem(key);
  } catch (error) {
    return null;
  }
};

const safeStorageSet = (storage, key, value) => {
  try {
    storage.setItem(key, value);
  } catch (error) {
    // Ignore storage failures in private/restricted browsing contexts.
  }
};

const safeStorageRemove = (storage, key) => {
  try {
    storage.removeItem(key);
  } catch (error) {
    // Ignore storage failures in private/restricted browsing contexts.
  }
};

const parseStoredJson = (value) => {
  if (!value) return null;
  try {
    return JSON.parse(value);
  } catch (error) {
    return null;
  }
};

export const getAnalyticsConsent = () => {
  if (!hasWindow()) return true;
  const storedConsent = safeStorageGet(
    window.localStorage,
    PRODUCT_ANALYTICS_CONSENT_KEY
  );
  return storedConsent === null ? true : storedConsent === "true";
};

const isAnalyticsConfigured = () => Boolean(posthogKey);

const isAnalyticsReady = () => initialized && isAnalyticsConfigured();

const applyAnalyticsConsent = (consent) => {
  if (!isAnalyticsReady()) return;
  if (consent) {
    posthog.opt_in_capturing();
  } else {
    posthog.opt_out_capturing();
  }
};

export const setAnalyticsConsent = (consent) => {
  if (!hasWindow()) return;
  safeStorageSet(
    window.localStorage,
    PRODUCT_ANALYTICS_CONSENT_KEY,
    consent ? "true" : "false"
  );
  applyAnalyticsConsent(Boolean(consent));
};

export const detectCurrentAnalyticsApp = ({
  hostname,
  pathname,
} = {}) => {
  const currentHostname =
    hostname || (hasWindow() ? window.location.hostname : "");
  const currentPathname =
    pathname || (hasWindow() ? window.location.pathname : "");

  if (
    isMissingVoicesHost(currentHostname) ||
    currentPathname === MISSING_VOICES_PATH ||
    currentPathname === "/missing-voices" ||
    currentPathname === "/missing-perspectives"
  ) {
    return PRODUCT_ANALYTICS_APPS.MISSING_VOICES;
  }

  return PRODUCT_ANALYTICS_APPS.TEACH_LEAGUE;
};

export const initializeSourceAttributionFromLocation = (search) => {
  if (!hasWindow()) return null;

  const params = new URLSearchParams(
    typeof search === "string" ? search : window.location.search
  );
  const source = params.get("utm_source") || params.get("source");
  const medium = params.get("utm_medium") || params.get("medium");
  const campaign = params.get("utm_campaign") || params.get("campaign");

  if (!source && !medium && !campaign) {
    return getStoredAttribution();
  }

  const payload = {
    source: source || "direct",
    medium: medium || "direct",
    campaign: campaign || "",
  };

  safeStorageSet(
    window.sessionStorage,
    PRODUCT_ANALYTICS_ATTRIBUTION_KEY,
    JSON.stringify(payload)
  );

  return payload;
};

export const getStoredAttribution = () => {
  if (!hasWindow()) {
    return {
      source: "direct",
      medium: "direct",
      campaign: "",
    };
  }

  const storedValue = safeStorageGet(
    window.sessionStorage,
    PRODUCT_ANALYTICS_ATTRIBUTION_KEY
  );
  const storedAttribution = parseStoredJson(storedValue);
  if (
    storedAttribution?.source ||
    storedAttribution?.medium ||
    storedAttribution?.campaign
  ) {
    return {
      source: storedAttribution.source || "direct",
      medium: storedAttribution.medium || "direct",
      campaign: storedAttribution.campaign || "",
    };
  }

  return {
    source: "direct",
    medium: "direct",
    campaign: "",
  };
};

export const initProductAnalytics = () => {
  if (!hasWindow() || initialized || !isAnalyticsConfigured()) {
    return false;
  }

  posthog.init(posthogKey, {
    api_host: posthogHost,
    defaults: "2026-01-30",
    autocapture: false,
    capture_pageview: false,
    capture_pageleave: false,
    opt_out_capturing_by_default: true,
    disable_session_recording: true,
    loaded: (instance) => {
      const consent = getAnalyticsConsent();
      if (consent) {
        instance.opt_in_capturing();
      } else {
        instance.opt_out_capturing();
      }
      initializeSourceAttributionFromLocation(window.location.search);
      if (posthogDebug) {
        instance.debug();
      }
    },
  });

  initialized = true;
  return true;
};

export const identifyAnalyticsUser = (userId, properties = {}) => {
  if (!isAnalyticsReady() || !userId) return;
  posthog.identify(userId, properties);
};

export const resetAnalyticsUser = () => {
  if (!isAnalyticsReady()) return;
  posthog.reset();
  safeStorageRemove(window.sessionStorage, PRODUCT_ANALYTICS_ATTRIBUTION_KEY);
  initializeSourceAttributionFromLocation(window.location.search);
};

export const trackProductEvent = (eventName, properties = {}) => {
  if (!isAnalyticsReady() || !getAnalyticsConsent()) return;

  const app =
    properties.app || detectCurrentAnalyticsApp(properties.currentLocation);
  const attribution = getStoredAttribution();
  const payload = {
    app,
    session_id: getSessionId(),
    source: attribution.source,
    medium: attribution.medium,
    campaign: attribution.campaign,
    ...properties,
  };

  delete payload.currentLocation;
  posthog.capture(eventName, payload);
};
